import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ClapperboardIcon, PlayIcon } from 'lucide-react'

import { api } from '@/api'
import { portalApi } from '@/api/portal'
import type { PortalTraining } from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { formatVideoDuration, mediaUrl, PORTAL_TRAININGS_KEY } from '@/lib/training-video'

export const Route = createFileRoute('/portal/formations/')({
  component: PortalTrainingsPage,
})

/**
 * « Formations » au portail (lot 38d) : les vidéos publiées pour le client,
 * dans sa langue quand elle existe — vignette, titre, durée, projet.
 */
function PortalTrainingsPage() {
  const t = msg().trainings.portal
  const trainings = useQuery({
    queryKey: PORTAL_TRAININGS_KEY,
    queryFn: () => portalApi.trainings(),
  })

  return (
    <div className="space-y-5">
      <PageHeader title={t.title} description={t.intro} />
      {trainings.isPending ? (
        <Frame>
          <Skeleton className="h-40 w-full rounded-md" />
        </Frame>
      ) : trainings.isError ? (
        <Frame>
          <FormError>{t.failed}</FormError>
        </Frame>
      ) : trainings.data.length === 0 ? (
        <Frame>
          <EmptyState icon={ClapperboardIcon} title={t.empty} />
        </Frame>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="portal-trainings">
          {trainings.data.map((training) => (
            <TrainingCard key={training.id} training={training} />
          ))}
        </ul>
      )}
    </div>
  )
}

function TrainingCard({ training }: { training: PortalTraining }) {
  const t = msg().trainings.portal
  const duration = formatVideoDuration(training.duration_ms)
  return (
    <li data-testid="portal-training-card" data-key={training.id}>
      <Link
        to="/portal/formations/$videoId"
        params={{ videoId: training.id }}
        aria-label={t.open(training.title)}
        className="group block overflow-hidden rounded-lg border bg-card transition-colors hover:border-primary/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <div className="relative aspect-video bg-muted">
          {training.poster_url && (
            <img
              src={mediaUrl(training.poster_url, training.media_token, api.baseUrl)}
              alt=""
              className="size-full object-cover"
              loading="lazy"
            />
          )}
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-12 place-items-center rounded-full bg-background/90 text-foreground shadow transition-transform group-hover:scale-105">
              <PlayIcon className="size-5" aria-hidden="true" />
            </span>
          </span>
          {duration && (
            <span className="absolute right-2 bottom-2 rounded bg-foreground/80 px-1.5 py-0.5 text-xs text-background tabular-nums">
              {duration}
            </span>
          )}
        </div>
        <div className="space-y-1 p-3">
          <p className="font-medium">{training.title}</p>
          {training.project && (
            <p className="text-sm text-muted-foreground">{t.project(training.project.name)}</p>
          )}
        </div>
      </Link>
    </li>
  )
}
